/**
 * EmptyState — leerer Zustand: Icon-Avatar ODER Bild-Stapel, Titel,
 * Erklärsatz, optional EINE Hauptaktion.
 *
 * Eingeführt in Slice-2 Redesign (2026-05-09). Redesign S2 Phase 4b
 * (2026-10-10, `tasks/design-handoff-redesign-s2.md` § Phase 4): weißes
 * 16px-Panel, Titel 19/700, Erklärsatz ≥ 13px, optional
 * `illustration="stack"` (dekorativer Bild-Stapel, kein API-Call).
 *
 * Pure präsentational, kein State.
 */
import type { ReactNode } from 'react';
import { I } from '../../lib/icons';

type Props = {
  /** Icon (z.B. `<I.upload size={20} />`) — wird in Avatar-Wrapper zentriert. */
  icon?: ReactNode;
  /** `stack` = Bild-Stapel statt Icon-Avatar (Markenmoment, z.B. Händler-Links). */
  illustration?: 'stack';
  title: string;
  sub?: string;
  /** Hauptaktion (Button oder Link), rendert unter dem Erklärsatz. */
  action?: ReactNode;
  className?: string;
};

export function EmptyState({ icon, illustration, title, sub, action, className = '' }: Props) {
  return (
    <div
      data-testid="empty-state"
      className={`border border-ink-100 rounded-2xl bg-white px-6 py-10 md:p-12 flex flex-col items-center text-center gap-4 ${className}`}
    >
      {illustration === 'stack' ? (
        <ImageStack />
      ) : (
        icon && (
          <div className="w-11 h-11 rounded-full bg-ink-50 text-brand-green-600 flex items-center justify-center">
            {icon}
          </div>
        )
      )}
      <div className="max-w-md">
        <div className="text-[1.1875rem] leading-snug font-bold text-brand-green-800">{title}</div>
        {sub && <p className="text-sm text-ink-500 mt-1.5">{sub}</p>}
      </div>
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

/** Drei gefächerte Bildkarten — rein dekorativ. */
function ImageStack() {
  return (
    <div data-testid="empty-state-stack" aria-hidden="true" className="relative w-32 h-24 mb-1">
      <div className="absolute inset-x-4 inset-y-1 rounded-xl bg-brand-sand -rotate-[10deg] -translate-x-5" />
      <div className="absolute inset-x-4 inset-y-1 rounded-xl bg-brand-green-100 rotate-[10deg] translate-x-5" />
      <div className="absolute inset-x-4 inset-y-0 rounded-xl bg-white border border-ink-200 shadow-sm flex items-center justify-center text-brand-green-600">
        <I.image size={24} />
      </div>
    </div>
  );
}
